import { type CalendarDate, type Time, today } from '@internationalized/date';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, I18nProvider } from 'react-aria-components';
import CalendarContent from '../shared/CalendarContent/CalendarContent';
import HourField from '../shared/HourField/HourField';
import { jsDateToCalendarDate, TIME_ZONE, toJsDate } from '../shared/dateUtils';
import { usePopoverDateValue } from '../shared/usePopoverDateValue';

/**
 * DatePickerInline — inline date (and optional time) picker built on react-aria. Meant to be rendered inside a
 * `Dialog`: react-aria's `Calendar` brings full WAI-ARIA keyboard navigation for the day grid (arrows,
 * PageUp/Down, Home/End) and the segmented `TimeField` is keyboard-accessible too. Colors are themed entirely
 * with `--ui-*` tokens.
 *
 * The public contract uses plain JS `Date`: it accepts `value: Date | string | null` and emits a `Date | null`
 * via `onChange`. All `@internationalized/date` conversion lives in `../shared/dateUtils`.
 */

export type DatePickerInlineProps = {
  // Extra content rendered in the same centered row as the TimeField (e.g. a deliver-late checkbox).
  children?: React.ReactNode;
  // Shows a native year <select> in the calendar header (month stays on prev/next).
  hasYearPicker?: boolean;
  minDate?: Date | string | null;
  onChange?: (date: Date | null) => void;
  shouldDisablePast?: boolean;
  showHour?: boolean;
  value?: Date | string | null;
};

function DatePickerInline({ children, hasYearPicker = false, minDate = null, onChange = () => {}, shouldDisablePast = true, showHour = false, value = null }: DatePickerInlineProps): JSX.Element {
  const { t, i18n } = useTranslation();

  // Shared date+time working value (CalendarDate + Time split + dateUtils glue), same as DatePicker/DatePickerActions.
  const dateState = usePopoverDateValue(toJsDate(value));

  // Re-sync when the consumer updates `value` from outside.
  const [prevValue, setPrevValue] = React.useState<Date | string | null | undefined>(value);
  if (value !== prevValue) {
    setPrevValue(value);
    dateState.setFromJsDate(toJsDate(value));
  }

  // An explicit minDate wins; otherwise fall back to "no past dates" when shouldDisablePast is on.
  const minValue = jsDateToCalendarDate(toJsDate(minDate)) ?? (shouldDisablePast ? today(TIME_ZONE) : undefined);

  function handleDateChange(nextDate: CalendarDate): void {
    onChange(dateState.changeDate(nextDate));
  }

  function handleTimeChange(nextTime: Time | null): void {
    onChange(dateState.changeTime(nextTime));
  }

  return (
    <I18nProvider locale={i18n.language}>
      <div className="date-picker-inline">
        <Calendar
          aria-label={t('common:Select date')}
          className="month-calendar date-picker-inline__calendar"
          minValue={minValue}
          onChange={handleDateChange}
          value={dateState.dateValue}
        >
          <CalendarContent hasYearPicker={hasYearPicker} locale={i18n.language} />
        </Calendar>

        {(showHour || children) && (
          <div className="date-picker-inline__bottom">
            {showHour && <HourField onChange={handleTimeChange} value={dateState.timeValue} />}
            {children}
          </div>
        )}
      </div>
    </I18nProvider>
  );
}

export default DatePickerInline;
